import type { OnInit } from '@angular/core';
import type { WebLegacyWidgetEntity } from '@web/types';
import type { FigureEntity } from '@sites/entity-core';

import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';

import { themeColorCss } from '@sites/site-theme/src/utils/variables';
import { DEFAULT_SHADOW_SETTINGS } from '@common/types/shadow-settings.type';

import type { FigureForm } from '@sites/entity-signal-form';

import { WebBaseViewSignalFormComponent } from '@web/widgets/common/abstract/web-base-view-signal-form.component';
import { LayoutFacade } from '@web/store/layout/layout.facade';
import { WebLegacyWidgetSignalFormService } from '@web/core/services/web-legacy-widget-signal-form.service';
import { shadowValueVar } from '@sites/entity-core';

/**
 * Editor preview for the `figure` widget — fully signal-based. The form is
 * shared with the control panel via `WebLegacyWidgetSignalFormService` (ref-counted
 * by widget id). All visual styling lives in the template (inline bindings +
 * SVG clip-path masks); the JSS sheet is intentionally empty.
 */
@Component({
    selector: 'web-figure-view',
    templateUrl: './figure-view.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [],
})
export class FigureViewComponent
    extends WebBaseViewSignalFormComponent<WebLegacyWidgetEntity<FigureEntity['data']>>
    implements OnInit
{
    private readonly signalFormService: WebLegacyWidgetSignalFormService = inject(WebLegacyWidgetSignalFormService);
    private readonly layoutFacade: LayoutFacade = inject(LayoutFacade);

    public form!: FigureForm;

    private readonly isDeviceMobileSig = toSignal(this.layoutFacade.isDeviceMobile$, { initialValue: false });
    private readonly isDeviceTabletSig = toSignal(this.layoutFacade.isDeviceTablet$, { initialValue: false });

    public ngOnInit(): void {
        this.form = this.signalFormService.getOrCreateScoped<FigureForm>(this.widget().id, this.destroyRef);
    }

    // --- Derived style values (template bindings) ---

    public readonly borderValue = computed<string | undefined>(() => {
        if (!this.form?.isBorder().value()) {
            return undefined;
        }

        const width = this.form.borderWidth().value();
        const style = this.form.borderStyle().value() || 'solid';
        const color = themeColorCss(this.form.borderColor().value());

        return `${width}px ${style} ${color}`;
    });

    public readonly borderColorValue = computed(() => themeColorCss(this.form?.borderColor().value()));

    public readonly backgroundColorValue = computed(() => themeColorCss(this.form?.backgroundColor().value()));

    /** `square`/`round` render a customizable box-shadow; the rest keep the filter. */
    private readonly isBoxShadowShape = computed<boolean>(() => {
        const shape = this.form?.figure().value();
        return shape === 'square' || shape === 'round';
    });

    /**
     * `filter: drop-shadow(...)` for the clip-path shapes (it follows the masked
     * outline). `square`/`round` opt out — they use {@link boxShadowValue}.
     */
    public readonly shadowValue = computed<string | undefined>(() => {
        if (!this.form?.isShadow().value() || this.isBoxShadowShape()) {
            return undefined;
        }

        return 'drop-shadow(0px 0px 5px rgba(14, 14, 14, 0.56))';
    });

    /** `box-shadow` (with `spread`) for the rectangular / elliptical shapes. */
    public readonly boxShadowValue = computed<string | undefined>(() => {
        if (!this.form?.isShadow().value() || !this.isBoxShadowShape()) {
            return undefined;
        }

        const s = this.form?.shadowSettings?.()?.value() ?? DEFAULT_SHADOW_SETTINGS;

        return shadowValueVar(s);
    });

    /** Active-device width — used by the horizontal-line SVG sizing. */
    public readonly widthValue = computed<number>(() => {
        const position = this.form?.position().value();
        if (!position) return 0;
        if (this.isDeviceMobileSig()) return position.mobileWidth || position.width;
        if (this.isDeviceTabletSig()) return position.tabletWidth || position.width;
        return position.width;
    });

    /** Active-device height — used by the horizontal-line SVG sizing. */
    public readonly heightValue = computed<number>(() => {
        const position = this.form?.position().value();
        if (!position) return 0;
        if (this.isDeviceMobileSig()) return position.mobileHeight || position.height;
        if (this.isDeviceTabletSig()) return position.tabletHeight || position.height;
        return position.height;
    });

    // --- SVG mask helpers ---

    public get maskId(): string {
        return `mask-${this.widget().id}`;
    }

    public get maskUrl(): string {
        return `url(#${this.maskId})`;
    }
}
